import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

export const TIERS = ["normal", "silver", "gold", "platinum"];
import { CONFIG } from "@/lib/api";
export const TIER_LABEL = new Proxy({}, { get: (_, k) => CONFIG.tiers?.find((t) => t.key === k)?.label || ({ normal: "Normal", silver: "Silver", gold: "Gold", platinum: "Platinum" })[k] });
export const TIER_CLS = {
  normal: "bg-slate-200 text-slate-800", silver: "bg-zinc-300 text-zinc-900",
  gold: "bg-amber-300 text-amber-950", platinum: "bg-cyan-200 text-cyan-950",
};

export const TierPrices = ({ value, onChange, testidPrefix }) => (
  <div className="grid grid-cols-4 gap-2">
    {TIERS.map((t) => (
      <div key={t}>
        <Label className="text-[11px]">{TIER_LABEL[t]} (RM)</Label>
        <Input type="number" step="0.01" min="0" value={value?.[t] ?? ""} placeholder={t === "normal" ? "0.00" : "= normal"}
          onChange={(e) => onChange({ ...value, [t]: e.target.value })} data-testid={`${testidPrefix}-${t}`} />
      </div>
    ))}
  </div>
);

export const DetailsEditor = ({ details, setDetails }) => {
  const add = () => setDetails([...details, { label: "", options: "" }]);
  const upd = (i, patch) => setDetails(details.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const remove = (i) => setDetails(details.filter((_, j) => j !== i));
  return (
    <div className="space-y-2 border rounded-lg p-3 bg-muted/30" data-testid="details-editor">
      <p className="text-[11px] text-muted-foreground">Add any detail this product needs (e.g. Material, Size, Laminate, Pages, Binding). Rename or remove titles freely. Options are comma separated and shown as choices when ordering.</p>
      {details.map((d, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 items-end" data-testid={`detail-row-${i}`}>
          <div><Label className="text-[11px]">Detail title</Label><Input value={d.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="e.g. Laminate" data-testid={`detail-label-${i}`} /></div>
          <div><Label className="text-[11px]">Options (comma separated)</Label><Input value={d.options} onChange={(e) => upd(i, { options: e.target.value })} placeholder="e.g. Matt, Gloss, None" data-testid={`detail-options-${i}`} /></div>
          <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(i)} data-testid={`detail-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} data-testid="detail-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add Detail</Button>
    </div>
  );
};

export const VariantsEditor = ({ fields, setFields, variants, setVariants }) => {
  const fieldList = String(fields || "").split(",").map((s) => s.trim()).filter(Boolean);
  const add = () => setVariants([...variants, { id: crypto.randomUUID(), name: "", attributes: {}, prices: {} }]);
  const upd = (i, patch) => setVariants(variants.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const remove = (i) => setVariants(variants.filter((_, j) => j !== i));

  return (
    <div className="space-y-3 border rounded-lg p-3 bg-muted/30" data-testid="variants-editor">
      <div>
        <Label>Variant detail fields (optional, comma separated)</Label>
        <Input value={fields} onChange={(e) => setFields(e.target.value)} placeholder="e.g. Size, Finishing, Paper" data-testid="variant-fields" />
        <p className="text-[11px] text-muted-foreground mt-1">Define which details each variant of this product has. Leave empty if the product has no variants.</p>
      </div>
      {variants.map((v, i) => (
        <div key={v.id} className="border rounded-md p-3 space-y-2 bg-background" data-testid={`variant-row-${i}`}>
          <div className="flex items-end gap-2">
            <div className="flex-1"><Label>Variant name *</Label><Input required value={v.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="e.g. A4 Matte" data-testid={`variant-name-${i}`} /></div>
            <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(i)} data-testid={`variant-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
          {fieldList.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {fieldList.map((f) => (
                <div key={f}><Label className="text-[11px]">{f}</Label><Input value={v.attributes?.[f] || ""} onChange={(e) => upd(i, { attributes: { ...v.attributes, [f]: e.target.value } })} data-testid={`variant-attr-${i}-${f.toLowerCase().replace(/\s+/g, "-")}`} /></div>
              ))}
            </div>
          )}
          <TierPrices value={v.prices} onChange={(p) => upd(i, { prices: p })} testidPrefix={`variant-price-${i}`} />
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} data-testid="variant-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add Variant</Button>
    </div>
  );
};
